import { useState, type FormEvent } from "react";
import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import {
  useMarketDataSettings,
  useSaveMarketDataSettings,
  type MarketDataSettings,
  type MarketDataSettingsInput,
} from "./queries.js";

export function MarketDataSettingsPanel() {
  const query = useMarketDataSettings();
  const save = useSaveMarketDataSettings();
  const [notice, setNotice] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  return (
    <article className="content-card form-stack" aria-labelledby="market-data-title">
      <div className="connection-heading">
        <div>
          <p className="eyebrow">Bot market data</p>
          <h2 id="market-data-title">Market data providers</h2>
          <p className="section-description">
            Choose the candle source for new backtests and live runs in this environment.
          </p>
        </div>
        <Button
          tone="quiet"
          disabled={query.isFetching || save.isPending}
          onClick={() => {
            save.reset();
            setNotice("");
            void query.refetch().then((result) => {
              if (!result.isError) setReloadVersion((value) => value + 1);
            });
          }}
        >
          Reload market data settings
        </Button>
      </div>
      {query.isPending ? (
        <ResourceState
          kind="loading"
          title="Loading market data settings"
          description="Reading this environment’s provider choices."
        />
      ) : null}
      {query.isError ? (
        <ResourceState
          kind="error"
          title="Market data settings unavailable"
          description="Reload to try again."
        />
      ) : null}
      {query.data === undefined || query.isError ? null : (
        <>
          <p className="field-hint">
            Environment: <strong>{query.data.environment}</strong>
          </p>
          <ProviderForm
            key={`${query.data.revision}:${reloadVersion}`}
            settings={query.data}
            pending={save.isPending}
            onSave={(values) => {
              setNotice("");
              save.mutate(values, {
                onSuccess: () =>
                  setNotice("Market data settings saved. New runs will use these providers."),
              });
            }}
          />
          <p className="field-hint">
            Active live runs and existing backtests keep their original provider. To move a live bot
            to the new provider, stop its current run and start it again.
          </p>
          <p className="field-hint">
            DXLink supplies native candles with provider-defined adjustments. Volume can differ from
            Alpaca SIP. Intraday history is limited: 1-minute bars to 43 days, 5/15-minute bars to
            90 days, and hourly bars to 285 days, with up to 8,000 candles. Daily candles have
            longer history. Strategy warm-up also needs to fit these limits.
          </p>
          <p className="field-hint">
            The Alpaca exchange calendar is still used for holidays and early closes. Ticker charts,
            manual quotes and AI Analysis keep their existing data sources.
          </p>
        </>
      )}
      {save.isError ? (
        <p className="form-message form-message--error" role="alert">
          {save.error.message} Your selections are retained. Reload if another administrator changed
          the settings.
        </p>
      ) : null}
      {notice ? (
        <p className="form-message form-message--success" role="status">
          {notice}
        </p>
      ) : null}
    </article>
  );
}
function ProviderForm({
  settings,
  pending,
  onSave,
}: {
  readonly settings: MarketDataSettings;
  readonly pending: boolean;
  readonly onSave: (input: MarketDataSettingsInput) => void;
}) {
  const [backtest, setBacktest] = useState(settings.backtestProvider);
  const [live, setLive] = useState(settings.liveProvider);
  const [configure, setConfigure] = useState(!settings.dxlinkAvailable);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const needsCredentials = configure && (backtest === "dxlink" || live === "dxlink");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      backtestProvider: backtest,
      liveProvider: live,
      expectedRevision: settings.revision,
      ...(needsCredentials
        ? {
            dxlinkCredentials: {
              clientSecret: clientSecret.trim(),
              refreshToken: refreshToken.trim(),
              ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
            },
          }
        : {}),
    });
  };
  return (
    <form className="form-stack" onSubmit={submit}>
      <div className="field-grid">
        <div className="field-stack">
          <label htmlFor="backtest-data-provider">Backtesting provider</label>
          <select
            id="backtest-data-provider"
            value={backtest}
            disabled={pending}
            onChange={(event) => setBacktest(event.target.value as typeof backtest)}
          >
            <option value="alpaca">Alpaca</option>
            <option value="dxlink">tastytrade DXLink</option>
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="live-data-provider">Live trading provider</label>
          <select
            id="live-data-provider"
            value={live}
            disabled={pending}
            onChange={(event) => setLive(event.target.value as typeof live)}
          >
            <option value="alpaca">Alpaca</option>
            <option value="dxlink">tastytrade DXLink</option>
          </select>
        </div>
      </div>
      {backtest === "dxlink" || live === "dxlink" ? (
        <>
          <p className="field-hint">
            {settings.dxlinkAvailable
              ? "A saved data connection or your connected production tastytrade account is available."
              : "Add production tastytrade OAuth credentials for market data. Certification accounts do not provide DXLink quotes. This data connection is separate from the account used to place trades."}
          </p>
          {settings.dxlinkAvailable ? (
            <label className="check-field">
              <input
                type="checkbox"
                checked={configure}
                disabled={pending}
                onChange={(event) => setConfigure(event.target.checked)}
              />
              <span>Replace the DXLink data connection</span>
            </label>
          ) : null}
          {needsCredentials ? (
            <div className="field-grid">
              <div className="field-stack">
                <label htmlFor="dxlink-client-id">OAuth client ID (optional)</label>
                <input
                  id="dxlink-client-id"
                  autoComplete="off"
                  disabled={pending}
                  value={clientId}
                  onChange={(event) => setClientId(event.target.value)}
                />
              </div>
              <div className="field-stack">
                <label htmlFor="dxlink-client-secret">Production OAuth client secret</label>
                <input
                  id="dxlink-client-secret"
                  type="password"
                  autoComplete="new-password"
                  required
                  disabled={pending}
                  value={clientSecret}
                  onChange={(event) => setClientSecret(event.target.value)}
                />
              </div>
              <div className="field-stack">
                <label htmlFor="dxlink-refresh-token">Production OAuth refresh token</label>
                <input
                  id="dxlink-refresh-token"
                  type="password"
                  autoComplete="new-password"
                  required
                  disabled={pending}
                  value={refreshToken}
                  onChange={(event) => setRefreshToken(event.target.value)}
                />
              </div>
              <p className="field-hint">
                The server verifies streaming access and stores these credentials in AWS Secrets
                Manager. Credentials are never returned by the settings API.
              </p>
            </div>
          ) : null}
        </>
      ) : null}
      <Button type="submit" disabled={pending}>
        {pending ? "Saving…" : "Save market data settings"}
      </Button>
    </form>
  );
}
